import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { setAgentFilterSearchParam } from "@/pages/agent/utils/agent-search-params.utils";
import { normalizeStatusValue } from "@/pages/agent/utils/agent-status.utils";

export const ALL_PROJECT_STATUSES_FILTER = "all-project-statuses";

export const ProjectSortKey = {
  RecentlyActive: "recently-active",
  Alphabetical: "alphabetical",
} as const;

export type ProjectSortKey = (typeof ProjectSortKey)[keyof typeof ProjectSortKey];

export const DEFAULT_PROJECT_SORT: ProjectSortKey = ProjectSortKey.RecentlyActive;

const PROJECT_STATUS_SEARCH_PARAM = "projectStatus";
const PROJECT_SEARCH_QUERY_SEARCH_PARAM = "projectSearch";
const PROJECT_SORT_SEARCH_PARAM = "projectSort";

const isProjectSortKey = (value: string): value is ProjectSortKey =>
  value === ProjectSortKey.RecentlyActive || value === ProjectSortKey.Alphabetical;

export const useAgentProjectsSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const statusFilter = normalizeStatusValue({
    status: searchParams.get(PROJECT_STATUS_SEARCH_PARAM) ?? ALL_PROJECT_STATUSES_FILTER,
  });
  const searchQuery = searchParams.get(PROJECT_SEARCH_QUERY_SEARCH_PARAM) ?? "";
  const rawSortFilter = searchParams.get(PROJECT_SORT_SEARCH_PARAM) ?? DEFAULT_PROJECT_SORT;
  const sortFilter: ProjectSortKey = isProjectSortKey(rawSortFilter)
    ? rawSortFilter
    : DEFAULT_PROJECT_SORT;

  const updateProjectFilters = useCallback(
    ({
      nextStatusFilter,
      nextSearchQuery,
      nextSortFilter,
    }: {
      nextStatusFilter?: string;
      nextSearchQuery?: string;
      nextSortFilter?: string;
    }) => {
      setSearchParams(
        previousSearchParams => {
          const nextSearchParams = new URLSearchParams(previousSearchParams);

          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: PROJECT_STATUS_SEARCH_PARAM,
            value: nextStatusFilter,
            defaultValue: ALL_PROJECT_STATUSES_FILTER,
          });
          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: PROJECT_SEARCH_QUERY_SEARCH_PARAM,
            value: nextSearchQuery,
            defaultValue: "",
          });
          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: PROJECT_SORT_SEARCH_PARAM,
            value: nextSortFilter,
            defaultValue: DEFAULT_PROJECT_SORT,
          });

          return nextSearchParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return {
    statusFilter,
    searchQuery,
    sortFilter,
    updateProjectFilters,
  };
};
